// Leitura do XML de NF-e (modelo 55) de compra: cabeçalho, emitente (fornecedor) e itens.
// Aceita tanto o nfeProc (nota autorizada, com protocolo) quanto o NFe sozinho.

export interface ItemNfe {
    numeroItem: number
    codigoProduto: string
    ean: string | null
    descricao: string
    ncm: string | null
    unidade: string
    quantidade: number
    valorUnitario: number
    valorTotal: number
}

export interface NotaNfe {
    chave: string
    modelo: string
    numero: string
    serie: string
    dataEmissao: string | null
    fornecedorCnpj: string
    fornecedorNome: string
    destinatarioCnpj: string | null
    valorTotal: number
    itens: ItemNfe[]
}

const filho = (el: Element | null | undefined, tag: string): Element | null =>
    el ? el.getElementsByTagName(tag)[0] ?? null : null

const texto = (el: Element | null | undefined, tag: string): string =>
    filho(el, tag)?.textContent?.trim() ?? ""

const numero = (v: string): number => {
    const n = parseFloat(v)
    return Number.isFinite(n) ? n : 0
}

const eanOuNull = (v: string): string | null => {
    const d = v.replace(/\D/g, "")
    return d.length >= 8 && d.length <= 14 ? d : null
}

/** Lê o XML; lança Error com mensagem amigável se não for uma NF-e válida. */
export function lerNfeXml(conteudo: string): NotaNfe {
    const doc = new DOMParser().parseFromString(conteudo, "application/xml")
    if (doc.getElementsByTagName("parsererror").length > 0) {
        throw new Error("O arquivo não é um XML válido.")
    }

    const infNFe = doc.getElementsByTagName("infNFe")[0]
    if (!infNFe) {
        throw new Error("O XML não é de uma NF-e (tag infNFe não encontrada).")
    }

    const ide = filho(infNFe, "ide")
    const emit = filho(infNFe, "emit")
    const dest = filho(infNFe, "dest")
    const icmsTot = filho(filho(infNFe, "total"), "ICMSTot")

    const chaveProtocolo = texto(doc.documentElement, "chNFe")
    const chaveId = (infNFe.getAttribute("Id") || "").replace(/\D/g, "")
    const chave = (chaveProtocolo || chaveId).replace(/\D/g, "")
    if (chave.length !== 44) {
        throw new Error("Não foi possível identificar a chave de acesso da NF-e.")
    }

    const modelo = texto(ide, "mod")
    if (modelo && modelo !== "55") {
        throw new Error(
            `Modelo ${modelo} não suportado: importe a NF-e de compra (modelo 55), não o cupom (NFC-e).`
        )
    }

    const detalhes = Array.from(infNFe.getElementsByTagName("det"))
    const itens: ItemNfe[] = detalhes.map((det, indice) => {
        const prod = filho(det, "prod")
        return {
            numeroItem: Number(det.getAttribute("nItem")) || indice + 1,
            codigoProduto: texto(prod, "cProd"),
            ean: eanOuNull(texto(prod, "cEAN")),
            descricao: texto(prod, "xProd"),
            ncm: texto(prod, "NCM") || null,
            unidade: texto(prod, "uCom").toUpperCase(),
            quantidade: numero(texto(prod, "qCom")),
            valorUnitario: numero(texto(prod, "vUnCom")),
            valorTotal: numero(texto(prod, "vProd")),
        }
    })
    if (itens.length === 0) {
        throw new Error("A NF-e não possui itens.")
    }

    return {
        chave,
        modelo: modelo || "55",
        numero: texto(ide, "nNF"),
        serie: texto(ide, "serie"),
        dataEmissao: texto(ide, "dhEmi") || texto(ide, "dEmi") || null,
        fornecedorCnpj: (texto(emit, "CNPJ") || texto(emit, "CPF")).replace(/\D/g, ""),
        fornecedorNome: texto(emit, "xFant") || texto(emit, "xNome"),
        destinatarioCnpj: (texto(dest, "CNPJ") || texto(dest, "CPF")).replace(/\D/g, "") || null,
        valorTotal: numero(texto(icmsTot, "vNF")),
        itens,
    }
}

/** Lê o arquivo como texto; tenta UTF-8 e cai para windows-1252 (XMLs antigos de alguns ERPs). */
export async function lerArquivoXml(arquivo: Blob): Promise<string> {
    const buffer = await arquivo.arrayBuffer()
    try {
        return new TextDecoder("utf-8", { fatal: true }).decode(buffer)
    } catch {
        return new TextDecoder("windows-1252").decode(buffer)
    }
}
